import { useState } from 'react';
import { useRouter } from 'next/router';
import { usePermissions, useSubscriptionOperations } from 'hooks';
import { driversServices } from '@services';
import { HANDOVER_CARS_TYPE, HANDOVER_CARS_SIDE } from '@constants';
import { asyncForEach, base64URIToBlob, showError } from '@utils';
import { ENTITIES_PERMISIONS } from 'components/common/PermissionsGate/constants';
import { handleSubscription } from './utils';

/**
 * Hook that handles common operations for handover cars and inspections
 * @param {string} pageType - 'delivery', 'return', or 'inspection'
 * @param {string} inspectionType - 'delivery' or 'return' (only for inspection pages)
 */
export const useHandoverOperations = (pageType, inspectionType = null) => {
  const { getSubscription, subscriptionId, subscriptionRaw, driverAgents } =
    useSubscriptionOperations(false);
  const [loading, setLoading] = useState(false);
  const { findEntity } = usePermissions();
  const { query, push } = useRouter();
  const { step } = query;

  const subscription = handleSubscription(subscriptionRaw);

  // Configuration based on page type
  const getConfig = () => {
    if (pageType === 'inspection') {
      const type =
        inspectionType === 'delivery'
          ? HANDOVER_CARS_TYPE.DELIVERY
          : HANDOVER_CARS_TYPE.RETURNS;
      return {
        handoverType: type,
        field: `${inspectionType}Info`,
        basePath: `/inspections/${subscriptionId}`,
        homeUrl: '/inspections',
        maxSteps: 8,
        additionalQuery: { inspectionType },
      };
    }

    const handoverType =
      pageType === 'delivery'
        ? HANDOVER_CARS_TYPE.DELIVERY
        : HANDOVER_CARS_TYPE.RETURNS;
    return {
      handoverType,
      field: `${pageType}Info`,
      basePath: `/${
        pageType === 'delivery' ? 'handoverCarsDelivery' : 'handoverCarsReturns'
      }/${subscriptionId}`,
      homeUrl: `/${
        pageType === 'delivery' ? 'handoverCarsDelivery' : 'handoverCarsReturns'
      }`,
      maxSteps: pageType === 'return' ? 9 : 8,
      additionalQuery: {},
    };
  };

  const config = getConfig();

  const nextStep = () => {
    const currentStep = !step ? 1 : Number(step) + 1;
    push({
      pathname: config.basePath,
      query: { step: currentStep, ...config.additionalQuery },
    });
  };

  const goHome = () => {
    window.location.replace(config.homeUrl);
  };

  const patchSubscription = async (payload) => {
    if (!findEntity([ENTITIES_PERMISIONS.WriteDeliveryReturn])) {
      showError('You have not write access');
      return;
    }
    try {
      if (pageType === 'inspection') {
        await driversServices.patchDailySubscription(
          config.handoverType,
          subscriptionId,
          payload
        );
      } else {
        const type = pageType === 'delivery' ? 0 : 1;
        await driversServices.patchDailySubscription(
          type,
          subscriptionId,
          payload
        );
      }
    } catch (error) {
      showError(error?.message || error);
      throw new Error(error);
    }
  };

  const patchInspection = async (payload) => {
    if (!findEntity([ENTITIES_PERMISIONS.WriteDeliveryReturn])) {
      showError('You have not write access');
      return;
    }
    try {
      await driversServices.patchInspection(
        config.handoverType,
        subscriptionId,
        payload
      );
    } catch (error) {
      showError(error?.message || error);
      throw new Error(error);
    }
  };

  const postImageSideSubscription = async (
    photos,
    side,
    type = 'sideCheck'
  ) => {
    try {
      if (!findEntity([ENTITIES_PERMISIONS.WriteDeliveryReturn])) {
        showError('You have not write access');
        return;
      }

      const photosToUpload = photos?.filter((photo) => photo?.isLocal);
      const photosToDelete = photos?.filter((photo) => photo?.delete);

      if (photosToUpload?.length) {
        await asyncForEach(photosToUpload, async (photo) => {
          const file = base64URIToBlob(photo?.src);
          const payload = new FormData();
          payload.append('image', file, `${photo?.name}.jpeg`);
          payload.append('tags', '');
          payload.append('caption', '');
          payload.append('description', '');

          if (type === 'sideCheck') {
            await driversServices.uploadImageSideSubscription(
              config.handoverType,
              subscriptionId,
              side,
              payload
            );
          } else if (type === 'kmTires') {
            await driversServices.uploadImageKmTires(
              config.handoverType,
              subscriptionId,
              payload
            );
          } else if (type === 'extraNotes') {
            await driversServices.uploadImageExtraNotes(
              config.handoverType,
              subscriptionId,
              payload
            );
          }
        });
      }

      if (photosToDelete?.length) {
        await asyncForEach(photosToDelete, async (photo) => {
          if (type === 'sideCheck') {
            await driversServices.deleteImageSideSubscription(
              config.handoverType,
              subscriptionId,
              side,
              photo?.id
            );
          } else if (type === 'kmTires') {
            await driversServices.deleteImageKmTires(
              config.handoverType,
              subscriptionId,
              photo?.id
            );
          } else if (type === 'extraNotes') {
            await driversServices.deleteImageExtraNotes(
              config.handoverType,
              subscriptionId,
              photo?.id
            );
          }
        });
      }
    } catch (error) {
      showError(error?.message || error);
      throw new Error(error);
    }
  };

  const handleVehicleDetails = async (form) => {
    try {
      let payload;
      if (pageType === 'delivery') {
        payload = {
          userId: form?.driver,
          vehicleParkingAddress: form?.vehicleParkingAddress || undefined,
          comment: form?.comment || undefined,
          assignee: form?.assignee,
        };
      } else if (pageType === 'return') {
        payload = {
          userId: form?.driver,
          assignee: form?.assignee,
        };
      } else if (pageType === 'inspection') {
        payload = {
          assigneeId: form?.assigneeId,
          assigneeName: form?.assigneeName,
        };
      }

      if (pageType !== 'inspection') {
        await patchSubscription(payload);
      } else {
        await patchInspection(payload);
      }
      await getSubscription();

      nextStep();
    } catch (error) {
      showError(error);
    }
  };

  const handleSideStep = async (form, side, checkListConfig) => {
    try {
      setLoading(true);
      const photos = form?.photos;
      delete form.photos;

      const payload = {
        vehicleChecks: [
          {
            side,
            comments: form?.comments || null,
            checkList: checkListConfig(form),
          },
        ],
      };

      await patchSubscription(payload);
      if (photos) {
        await postImageSideSubscription(photos, side);
      }
      await getSubscription();
      nextStep();
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleKmFuelStep = async (form) => {
    try {
      setLoading(true);
      const photos = form?.photos;
      delete form.photos;

      const deliveryKm = subscriptionRaw?.deliveryInfo?.kmFuelTiresCheck?.km;
      const returnKm = subscriptionRaw?.returnInfo?.kmFuelTiresCheck?.km;

      const payload = {
        kmFuelTiresCheck: {
          km: Number(form?.km),
          fuel: form?.fuel,
          tires: [
            {
              position: form?.position_1,
              dimensions: form?.dimensions_1,
              dot: form?.dot_1,
            },
            {
              position: form?.position_2,
              dimensions: form?.dimensions_2,
              dot: form?.dot_2,
            },
            {
              position: form?.position_3,
              dimensions: form?.dimensions_3,
              dot: form?.dot_3,
            },
            {
              position: form?.position_4,
              dimensions: form?.dimensions_4,
              dot: form?.dot_4,
            },
          ],
        },
      };

      // Validation logic
      if (pageType === 'delivery' && returnKm && form?.km > returnKm) {
        return showError(
          'Kilometers of delivery cannot be more than kilometers of return.'
        );
      }

      if (
        (pageType === 'return' || inspectionType === 'return') &&
        deliveryKm &&
        form?.km < deliveryKm
      ) {
        return showError(
          'Kilometers of return cannot be less than kilometers of delivery.'
        );
      }

      if (
        pageType === 'inspection' &&
        inspectionType === 'delivery' &&
        deliveryKm &&
        form?.km < deliveryKm
      ) {
        return showError(
          'Kilometers of delivery cannot be more than kilometers of return.'
        );
      }

      await patchSubscription(payload);
      if (photos) {
        await postImageSideSubscription(photos, null, 'kmTires');
      }
      await getSubscription();
      nextStep();
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleExtraCommentStep = async (form) => {
    try {
      setLoading(true);
      const photos = form?.photos;
      if (photos) {
        delete form.photos;
        await postImageSideSubscription(photos, null, 'extraNotes');
      }
      const payload = {
        extraNotes: {
          comments: form?.comments,
        },
      };
      await patchSubscription(payload);
      await getSubscription();
      nextStep();
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  return {
    subscription,
    subscriptionId,
    loading,
    step: Number(step) || 0,
    config,
    nextStep,
    goHome,
    handleVehicleDetails,
    handleSideStep,
    handleKmFuelStep,
    handleExtraCommentStep,
    getSubscription,
    HANDOVER_CARS_SIDE,
    driverAgents,
  };
};
